import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { Modal } from '../components/Modal';
import { PageNav } from '../components/PageNav';
import { DataTable, type Column } from '../components/DataTable';
import { Filters, useFilters, type FilterField } from '../components/Filters';
import { SelectionBar, type BulkAction } from '../components/SelectionBar';
import { useSession } from '../lib/session';
import type { InvoiceRow } from '../lib/types';

/**
 * The three words an office uses about an invoice.
 *
 * The enum underneath has five states, three of which are bookkeeping detail.
 * What somebody wants to *say* is that the money is in, that it is owed, or
 * that it is not going to be collected — so those are the three offered, and
 * the mapping to the enum happens on the server where it can be audited.
 */
const CHOICES = [
  {
    value: 'paid' as const,
    label: 'Paid',
    hint: 'Records a payment for the balance, so the day-book and the arrears agree.',
  },
  {
    value: 'pending' as const,
    label: 'Pending',
    hint: 'Owed, and waiting. This is where an invoice sits until the money arrives.',
  },
  {
    value: 'not_payable' as const,
    label: 'Not payable',
    hint: 'Cancelled, with a reason, and released from anything allocated to it.',
  },
];

const METHODS = ['mpesa', 'cash', 'bank', 'credit'] as const;

const TONE: Record<InvoiceRow['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  issued: 'bg-amber-100 text-amber-900',
  part_paid: 'bg-amber-100 text-amber-900',
  paid: 'bg-secondary-100 text-secondary-800',
  void: 'bg-muted text-muted-foreground line-through',
};

const WORDING: Record<InvoiceRow['status'], string> = {
  draft: 'Draft',
  issued: 'Pending',
  part_paid: 'Part paid',
  paid: 'Paid',
  void: 'Not payable',
};

/**
 * Every invoice, and a way to raise one by hand.
 *
 * Billing already had a monthly run and a per-subscription button, both of
 * which produce the same thing: a line for the plan fee and nothing else. That
 * covers the subscription and none of the rest of the business — an
 * installation, a replaced ONT, a drop cable, a Sunday call-out. Those were
 * being written on paper, which is to say not recorded at all.
 */
export function InvoicesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [filter, setFilter] = useState<'all' | 'issued' | 'paid' | 'void'>('all');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settling, setSettling] = useState<InvoiceRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invoices = useQuery({
    queryKey: ['invoices', filter],
    queryFn: () => api.invoices(filter === 'all' ? {} : { status: filter }),
  });

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  // Memoised for the `?? []`: a fresh empty array each render would make the
  // total below recompute every time, for no new data.
  const all = useMemo(() => invoices.data ?? [], [invoices.data]);

  /*
   * The total owed is of everything, not of what a filter happens to be
   * showing. A headline figure that moves when somebody narrows the list is a
   * headline figure nobody can quote.
   */
  const owed = useMemo(
    () => all.reduce((total, row) => total + (row.outstandingCents ?? 0), 0),
    [all],
  );

  const FIELDS: FilterField<InvoiceRow>[] = [
    { key: 'number', label: 'Number', type: 'text', value: (row) => row.number },
    { key: 'customer', label: 'Customer', type: 'text', value: (row) => row.customerName },
    { key: 'due', label: 'Due', type: 'date', value: (row) => row.dueDate },
    { key: 'total', label: 'Total', type: 'number', value: (row) => row.totalCents / 100 },
    {
      key: 'outstanding',
      label: 'Outstanding',
      type: 'number',
      value: (row) => row.outstandingCents / 100,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'choice',
      options: Object.entries(WORDING).map(([value, label]) => ({ value, label })),
      value: (row) => row.status,
    },
  ];

  const { query, setQuery, clauses, setClauses, filtered: rows } = useFilters(all, FIELDS);

  if (invoices.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  /**
   * What can be done to a batch of invoices, and what deliberately cannot.
   *
   * Export and print, because a list of what is owed is worked from paper and
   * from a spreadsheet at least as often as from the screen.
   *
   * **No delete, and no bulk edit.** An invoice is a document somebody was
   * sent: the way to undo one is a credit note, which leaves both the original
   * and the correction where an accountant can see them. Deleting it would
   * remove the only record that the customer was ever asked for the money —
   * and the audit trail would show a number that no longer exists. Voiding is
   * offered per invoice, one at a time, with a reason, which is the right
   * amount of friction for something that changes what a customer owes.
   */
  const actions: BulkAction<InvoiceRow>[] = [
    { key: 'print', label: 'Print', prominent: true, run: () => window.print() },
  ];

  const columns: Column<InvoiceRow>[] = [
    {
      key: 'number',
      header: 'Number',
      sortBy: (row) => row.number,
      cell: (row) => <span className="font-mono text-xs">{row.number}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      sortBy: (row) => row.customerName.toLowerCase(),
      cell: (row) => row.customerName,
    },
    {
      key: 'due',
      header: 'Due',
      secondary: true,
      /*
       * On the date, not on how it is spelled. "01/02" before "31/01" is what
       * sorting the rendered string gives, and on a page about what is overdue
       * that is the wrong order in the one column it matters most.
       */
      sortBy: (row) => new Date(row.dueDate).getTime(),
      cell: (row) => (
        <span className="text-muted-foreground">
          {new Date(row.dueDate).toLocaleDateString('en-GB')}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      sortBy: (row) => row.totalCents,
      cell: (row) => <span className="tabular-nums">{row.total}</span>,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      sortBy: (row) => row.outstandingCents,
      cell: (row) => <span className="tabular-nums">{row.outstanding}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortBy: (row) => row.status,
      cell: (row) => (
        <span className={`rounded-full px-2 py-0.5 text-xs ${TONE[row.status]}`}>
          {WORDING[row.status]}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) =>
        can('payment:record') && row.status !== 'void' ? (
          <button
            type="button"
            className="btn-quiet print:hidden"
            onClick={(event) => {
              event.stopPropagation();
              setSettling(row);
            }}
          >
            Set status
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageNav crumbs={[{ label: 'Invoices' }]} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-1.5 font-heading text-2xl">
            Invoices
            <HelpHint>
              The monthly run raises the plan fees on its own. Raise one here for anything else —
              an installation, a replacement, a call-out.
            </HelpHint>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {rows.length} invoice{rows.length === 1 ? '' : 's'}
            {owed > 0 && ` · ${(owed / 100).toLocaleString('en-KE', { style: 'currency', currency: 'KES' })} outstanding`}
          </p>
        </div>
        {can('billing:issue') && (
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New invoice
          </button>
        )}
      </div>

      {error && (
        <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {(['all', 'issued', 'paid', 'void'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={filter === value ? 'btn-secondary' : 'btn-quiet'}
            onClick={() => setFilter(value)}
          >
            {value === 'all'
              ? 'All'
              : value === 'issued'
                ? 'Pending'
                : value === 'paid'
                  ? 'Paid'
                  : 'Not payable'}
          </button>
        ))}
      </div>

      <section className="card">
        {rows.length === 0 ? (
          <EmptyState
            icon={FileText}
            headline="Nothing here"
            body="Invoices raised by the monthly run and by hand both appear here."
          />
        ) : (
          <>
            <Filters<InvoiceRow>
              fields={FIELDS}
              query={query}
              onQuery={setQuery}
              clauses={clauses}
              onClauses={setClauses}
              total={all.length}
              showing={rows.length}
              placeholder="Search by number or customer"
            />

            <SelectionBar<InvoiceRow>
              rows={rows}
              selected={selected}
              onClear={() => setSelected(new Set())}
              rowKey={(row) => row.id}
              exportName="invoices"
              csv={[
                { header: 'Number', value: (row) => row.number },
                { header: 'Customer', value: (row) => row.customerName },
                { header: 'Due', value: (row) => row.dueDate.slice(0, 10) },
                { header: 'Total', value: (row) => row.totalCents / 100 },
                { header: 'Outstanding', value: (row) => row.outstandingCents / 100 },
                { header: 'Status', value: (row) => WORDING[row.status] },
              ]}
              actions={actions}
            />

            <DataTable<InvoiceRow>
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              selected={selected}
              onSelect={setSelected}
              defaultSort={{ key: 'due' }}
              caption={`${rows.length} ${rows.length === 1 ? 'invoice' : 'invoices'}`}
              empty={
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No invoice matches.
                </p>
              }
            />
          </>
        )}
      </section>

      {creating && (
        <NewInvoiceDialog
          onDone={async () => {
            setCreating(false);
            await refresh();
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {settling && (
        <StatusDialog
          invoice={settling}
          onDone={async () => {
            setSettling(null);
            await refresh();
          }}
          onClose={() => setSettling(null)}
        />
      )}
    </div>
  );
}

/** Where an invoice stands, and what that costs elsewhere in the books. */
function StatusDialog({
  invoice,
  onDone,
  onClose,
}: {
  invoice: InvoiceRow;
  onDone: () => void;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<'paid' | 'pending' | 'not_payable'>('paid');
  const [method, setMethod] = useState<string>('mpesa');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.setInvoiceStatus(invoice.id, {
        status: choice,
        ...(choice === 'paid' ? { method, reference: reference.trim() || undefined } : {}),
        ...(choice === 'not_payable' ? { reason } : {}),
      }),
    onSuccess: onDone,
    onError: (e: Error) => setError(e.message),
  });

  const needsReference = choice === 'paid' && (method === 'mpesa' || method === 'bank');
  const blocked =
    (choice === 'not_payable' && reason.trim().length < 3) ||
    (needsReference && reference.trim().length === 0);

  return (
    <Modal
      title={`${invoice.number} — ${invoice.customerName}`}
      subtitle={`${invoice.total} · ${invoice.outstanding} outstanding`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={blocked || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Where does it stand?</legend>
          {CHOICES.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                choice === option.value ? 'border-primary-500 bg-primary-50/50' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name="invoice-status"
                className="mt-1"
                checked={choice === option.value}
                onChange={() => setChoice(option.value)}
              />
              <span>
                <span className="block text-sm font-medium">{option.label}</span>
                <span className="block text-xs text-muted-foreground">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {choice === 'paid' && (
          <div className="space-y-3 rounded-xl border border-border p-3">
            <label className="block text-sm">
              How was it paid?
              <select
                className="input mt-1 w-full"
                value={method}
                onChange={(event) => setMethod(event.target.value)}
              >
                {METHODS.map((value) => (
                  <option key={value} value={value}>
                    {value === 'mpesa' ? 'M-Pesa' : value[0]!.toUpperCase() + value.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Reference {needsReference ? '' : '(optional)'}
              <input
                className="input mt-1 w-full font-mono"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder={method === 'mpesa' ? 'SJ42K1L9XZ' : 'Bank slip number'}
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                {needsReference
                  ? 'This is what proves the payment arrived, and the customer will quote it.'
                  : 'Anything that helps you find it again later.'}
              </span>
            </label>
          </div>
        )}

        {choice === 'not_payable' && (
          <label className="block text-sm">
            Why is it not going to be collected?
            <input
              className="input mt-1 w-full"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Raised in error — the customer was never connected"
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Kept on the record. Writing a debt off silently is how a month comes up short with
              nobody able to say why.
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}

type Line = { description: string; quantity: string; unitPriceKes: string };

/** An invoice somebody types out, line by line. */
function NewInvoiceDialog({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [customerId, setCustomerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Line[]>([
    { description: '', quantity: '1', unitPriceKes: '' },
  ]);
  const [error, setError] = useState<string | null>(null);

  const customers = useQuery({ queryKey: ['customers'], queryFn: api.customers });

  const total = lines.reduce(
    (sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unitPriceKes) || 0),
    0,
  );

  const create = useMutation({
    mutationFn: () =>
      api.createInvoice({
        customerId,
        lines: lines
          .filter((line) => line.description.trim() && Number(line.unitPriceKes) > 0)
          .map((line) => ({
            description: line.description.trim(),
            quantity: Number(line.quantity) || 1,
            // Cents on the way in, because everything downstream is integers.
            unitPriceCents: Math.round(Number(line.unitPriceKes) * 100),
          })),
        dueDate: dueDate || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: onDone,
    onError: (e: Error) => setError(e.message),
  });

  const setLine = (index: number, key: keyof Line, value: string) =>
    setLines((current) =>
      current.map((line, i) => (i === index ? { ...line, [key]: value } : line)),
    );

  const ready =
    customerId.length > 0 &&
    lines.some((line) => line.description.trim() && Number(line.unitPriceKes) > 0);

  return (
    <Modal
      wide
      title="New invoice"
      subtitle="For anything the monthly run does not cover"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!ready || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Raise it
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <label className="block text-sm">
          Customer
          <select
            className="input mt-1 w-full"
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
          >
            <option value="">Choose a customer</option>
            {(customers.data?.customers ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </label>

        <div>
          <p className="text-sm font-medium">Lines</p>
          <div className="mt-2 space-y-2">
            {lines.map((line, index) => (
              <div key={index} className="flex flex-wrap items-end gap-2">
                <label className="min-w-[12rem] flex-1 text-xs text-muted-foreground">
                  What for
                  <input
                    className="input mt-1 w-full text-sm"
                    value={line.description}
                    onChange={(event) => setLine(index, 'description', event.target.value)}
                    placeholder="Installation — 60m drop cable and an ONT"
                  />
                </label>
                <label className="w-20 text-xs text-muted-foreground">
                  Qty
                  <input
                    className="input mt-1 w-full text-sm"
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(event) => setLine(index, 'quantity', event.target.value)}
                  />
                </label>
                <label className="w-28 text-xs text-muted-foreground">
                  Price (KES)
                  <input
                    className="input mt-1 w-full text-sm"
                    inputMode="decimal"
                    value={line.unitPriceKes}
                    onChange={(event) => setLine(index, 'unitPriceKes', event.target.value)}
                  />
                </label>
                {lines.length > 1 && (
                  <button
                    type="button"
                    className="btn-quiet"
                    aria-label={`Remove line ${index + 1}`}
                    onClick={() => setLines((c) => c.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn-quiet mt-2"
            onClick={() =>
              setLines((c) => [...c, { description: '', quantity: '1', unitPriceKes: '' }])
            }
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Another line
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="flex-1 text-sm">
            Due date
            <input
              type="date"
              className="input mt-1 w-full"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Left empty, it falls due on your usual terms.
            </span>
          </label>
          <label className="flex-1 text-sm">
            Note (optional)
            <input
              className="input mt-1 w-full"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Agreed with the customer on the phone"
            />
          </label>
        </div>

        {/*
          Before VAT, and it says so. Showing a total that is not the total is
          worse than showing none — the figure on the invoice is worked out on
          the server, on the whole invoice rather than per line.
        */}
        <p className="text-sm text-muted-foreground">
          Lines come to{' '}
          <strong className="tabular-nums text-foreground">
            {total.toLocaleString('en-KE', { style: 'currency', currency: 'KES' })}
          </strong>{' '}
          before VAT.
        </p>
      </div>
    </Modal>
  );
}
